/**
 * App-wide soft BGM — BUDB tracks + MTT Soldier.
 *
 * Tracks (Worker static under /audio/bgm/):
 * - budb-soft-1.mp3  → table / general poker soft bed
 * - mtt-soldier.mp3  → lobby
 * - budb-soft-2.mp3  → intro stinger + load-complete loop
 *   Intro: 2:04–2:09 (fade out) then loop 2:17–2:25
 *
 * Settings: poker_settings.musicEnabled / musicVolume (+ master soundEnabled).
 * No Howler — HTMLAudioElement only.
 */

const BGM = {
  table: "/audio/bgm/budb-soft-1.mp3",
  lobby: "/audio/bgm/mtt-soldier.mp3",
  intro: "/audio/bgm/budb-soft-2.mp3",
} as const;

/** Intro stinger from track 2 */
const INTRO_START = 2 * 60 + 4; // 2:04
const INTRO_END = 2 * 60 + 9; // 2:09
/** Soft loop after load complete */
const LOOP_START = 2 * 60 + 17; // 2:17
const LOOP_END = 2 * 60 + 25; // 2:25

export type BgmScene = "lobby" | "table" | "landing" | "account" | "slots" | "other";

type MusicSettings = {
  masterOn: boolean;
  musicOn: boolean;
  musicVol: number;
};

function readMusicSettings(): MusicSettings {
  try {
    const raw = localStorage.getItem("poker_settings");
    if (raw) {
      const s = JSON.parse(raw) as Record<string, unknown>;
      const masterOn = s.soundEnabled !== false;
      const musicOn = s.musicEnabled !== false;
      const musicVol = Math.min(
        1,
        Math.max(0, (Number(s.musicVolume) ?? 35) / 100),
      );
      return { masterOn, musicOn, musicVol };
    }
  } catch {
    /* ignore */
  }
  return { masterOn: true, musicOn: true, musicVol: 0.35 };
}

/** Invalidate cache after UI writes settings */
export function invalidateMusicSettingsCache(): void {
  /* settings re-read each apply — no cache */
}

export function writeMusicSettings(patch: {
  musicEnabled?: boolean;
  musicVolume?: number;
  soundEnabled?: boolean;
}): void {
  try {
    const raw = localStorage.getItem("poker_settings");
    const cur = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    if (patch.musicEnabled !== undefined) cur.musicEnabled = patch.musicEnabled;
    if (patch.musicVolume !== undefined) cur.musicVolume = patch.musicVolume;
    if (patch.soundEnabled !== undefined) cur.soundEnabled = patch.soundEnabled;
    localStorage.setItem("poker_settings", JSON.stringify(cur));
    window.dispatchEvent(new CustomEvent("poker-settings-changed"));
  } catch {
    /* ignore */
  }
}

export function getMusicUiState(): {
  muted: boolean;
  volume: number;
  masterOn: boolean;
} {
  const s = readMusicSettings();
  return {
    muted: !s.masterOn || !s.musicOn,
    volume: Math.round(s.musicVol * 100),
    masterOn: s.masterOn,
  };
}

let unlocked = false;
let bed: HTMLAudioElement | null = null;
let intro: HTMLAudioElement | null = null;
let scene: BgmScene = "other";
let introDone = false;
let rafLoop: number | null = null;
let fadeTimer: number | null = null;

function clearFade(): void {
  if (fadeTimer != null) {
    window.clearInterval(fadeTimer);
    fadeTimer = null;
  }
}

function effectiveVol(): number {
  const s = readMusicSettings();
  if (!s.masterOn || !s.musicOn) return 0;
  return s.musicVol;
}

function ensureBed(src: string): HTMLAudioElement {
  if (!bed || bed.dataset.src !== src) {
    if (bed) {
      bed.pause();
      bed.src = "";
    }
    bed = new Audio(src);
    bed.dataset.src = src;
    bed.loop = true;
    bed.preload = "auto";
    bed.volume = 0;
  }
  return bed;
}

function ensureIntro(): HTMLAudioElement {
  if (!intro) {
    intro = new Audio(BGM.intro);
    intro.preload = "auto";
    intro.loop = false;
  }
  return intro;
}

function stopLoopWatch(): void {
  if (rafLoop != null) {
    cancelAnimationFrame(rafLoop);
    rafLoop = null;
  }
}

/** Keep intro track in the 2:17–2:25 window */
function watchIntroLoop(): void {
  stopLoopWatch();
  const a = intro;
  if (!a) return;
  const tick = () => {
    if (!a || a.paused) {
      rafLoop = null;
      return;
    }
    if (a.currentTime >= LOOP_END - 0.05) {
      a.currentTime = LOOP_START;
    }
    rafLoop = requestAnimationFrame(tick);
  };
  rafLoop = requestAnimationFrame(tick);
}

function fadeTo(el: HTMLAudioElement, target: number, ms = 900): Promise<void> {
  clearFade();
  return new Promise((resolve) => {
    const start = el.volume;
    const t0 = performance.now();
    fadeTimer = window.setInterval(() => {
      const t = Math.min(1, (performance.now() - t0) / ms);
      el.volume = Math.max(0, Math.min(1, start + (target - start) * t));
      if (t >= 1) {
        clearFade();
        resolve();
      }
    }, 40);
  });
}

function trackForScene(s: BgmScene): string | null {
  switch (s) {
    case "lobby":
      return BGM.lobby;
    case "table":
    case "account":
      return BGM.table;
    case "landing":
    case "other":
      return BGM.table;
    case "slots":
      return null; // slots has its own BGM
    default:
      return BGM.table;
  }
}

export function unlockPokerBgm(): void {
  if (unlocked || typeof window === "undefined") return;
  unlocked = true;
  // Warm unlock with silent play attempt
  try {
    const a = ensureBed(BGM.table);
    a.volume = 0.01;
    void a.play().then(() => {
      a.pause();
      a.volume = 0;
    }).catch(() => {
      unlocked = false;
    });
  } catch {
    unlocked = false;
  }
}

/**
 * Play load/intro: 2:04–2:09 fade out → loop 2:17–2:25 until scene bed takes over.
 */
export async function playIntroStinger(): Promise<void> {
  if (typeof window === "undefined") return;
  const vol = effectiveVol();
  if (vol <= 0) {
    introDone = true;
    return;
  }
  unlockPokerBgm();
  const a = ensureIntro();
  stopLoopWatch();
  try {
    a.pause();
    a.currentTime = INTRO_START;
    a.volume = vol;
    await a.play();
  } catch {
    introDone = true;
    return;
  }

  // Watch for intro end → fade → loop segment
  const onTime = () => {
    if (a.currentTime >= INTRO_END - 0.35) {
      a.removeEventListener("timeupdate", onTime);
      void (async () => {
        await fadeTo(a, 0, 500);
        a.pause();
        a.currentTime = LOOP_START;
        a.volume = vol * 0.85;
        try {
          await a.play();
          watchIntroLoop();
        } catch {
          /* blocked */
        }
        introDone = true;
      })();
    }
  };
  a.addEventListener("timeupdate", onTime);
}

export async function setBgmScene(next: BgmScene): Promise<void> {
  scene = next;
  const vol = effectiveVol();
  const src = trackForScene(next);

  // Slots: stop app BGM (slots-audio owns music there)
  if (!src || vol <= 0) {
    if (bed) {
      await fadeTo(bed, 0, 400);
      bed.pause();
    }
    if (intro && !intro.paused) {
      await fadeTo(intro, 0, 300);
      intro.pause();
      stopLoopWatch();
    }
    return;
  }

  // After intro, or if intro never started — run scene bed
  if (intro && !intro.paused && !introDone) {
    // Let intro finish; bed will start when intro stops via applySettings
    return;
  }

  if (intro && !intro.paused) {
    await fadeTo(intro, 0, 400);
    intro.pause();
    stopLoopWatch();
  }

  const a = ensureBed(src);
  a.loop = true;
  try {
    if (a.paused) {
      a.volume = 0;
      await a.play();
    }
    await fadeTo(a, vol, 800);
  } catch {
    /* autoplay blocked until gesture */
  }
}

export async function applyMusicSettings(): Promise<void> {
  const vol = effectiveVol();
  if (vol <= 0) {
    if (bed && !bed.paused) {
      await fadeTo(bed, 0, 300);
      bed.pause();
    }
    if (intro && !intro.paused) {
      await fadeTo(intro, 0, 200);
      intro.pause();
      stopLoopWatch();
    }
    return;
  }
  if (intro && !intro.paused) {
    intro.volume = vol * (introDone ? 0.85 : 1);
    return;
  }
  if (bed && !bed.paused) {
    bed.volume = vol;
  } else {
    await setBgmScene(scene);
  }
}

export function sceneFromPath(pathname: string): BgmScene {
  if (pathname === "/" || pathname === "") return "landing";
  if (pathname.startsWith("/lobby")) return "lobby";
  if (pathname.startsWith("/table")) return "table";
  if (pathname.startsWith("/slots")) return "slots";
  if (pathname.startsWith("/account") || pathname.startsWith("/blackjack"))
    return "account";
  if (pathname.startsWith("/calling")) return "landing";
  return "other";
}

/** Toggle music mute (persists). Returns new muted state. */
export function toggleMusicMute(): boolean {
  const ui = getMusicUiState();
  const nextMuted = !ui.muted;
  if (nextMuted) {
    writeMusicSettings({ musicEnabled: false });
  } else {
    writeMusicSettings({ musicEnabled: true, soundEnabled: true });
  }
  void applyMusicSettings();
  if (!nextMuted) void setBgmScene(scene);
  return nextMuted;
}

export function setMusicVolumePercent(pct: number): void {
  const v = Math.min(100, Math.max(0, Math.round(pct)));
  writeMusicSettings({ musicVolume: v, musicEnabled: v > 0 });
  void applyMusicSettings();
}

export const POKER_BGM_URLS = BGM;
